import { useEffect, useState, useMemo, useRef, useContext } from "react";
import styles from "./guide.module.css";
import { CardOne } from "../../../components/cards/cardOne";
import { CardTwo } from "../../../components/cards/cardTwo";
import { GoBackButton } from "../../../components/buttons/goBack";
import { navigationGamepadLoop } from "../../../utilities/utilities";
import { themeContext } from "../../../context/theme";

export const Guide = ({ setShowGuide, game }) => {
  const [startGamePadLoop, setStartGamePadLoop] = useState(true);
  const { theme } = useContext(themeContext);
  const guideRef = useRef();
  const gamepadLoopState = useMemo(() => {
    return { run: true };
  }, []);
  const gameModesCloseButtonClickHandler = () => {
    setShowGuide(false);
  };

  const controllerSettingsOverlayKeyDownHandler = (event) => {
    if (event.stopPropagation) {
      event.stopPropagation();
      if (event.key !== "Escape") {
        return;
      }
    }
    if (event.key === "ArrowDown") {
      if (guideRef.current) {
        guideRef.current.scrollBy({
          top: 50,
        });
      }
    } else if (event.key === "ArrowUp") {
      if (guideRef.current) {
        guideRef.current.scrollBy({
          top: -50,
        });
      }
    } else if (event.key === "Enter") {
    } else if (
      event.key === "Back" ||
      event.key === "Escape"
    ) {
      game.sounds.clickSound.play();
      gamepadLoopState.run = false;
      setShowGuide(false);
    }
  };

  useEffect(() => {
    if (gamepadLoopState.run) {
      navigationGamepadLoop(
        game,
        controllerSettingsOverlayKeyDownHandler,
        gamepadLoopState
      );
    }
  }, [startGamePadLoop]);

  useEffect(() => {
    guideRef.current.focus();
  }, []);

  useEffect(() => {
    return () => {
      gamepadLoopState.run = false;
    };
  }, []);
  return (
    <CardOne
      customTag="section"
      attributes={{
        className: styles["guide-section"],
        onKeyDown: controllerSettingsOverlayKeyDownHandler,
        tabIndex: -1,
        ref: guideRef,
      }}
    >
      <GoBackButton
        onClickHandler={gameModesCloseButtonClickHandler}
        game={game}
      ></GoBackButton>
      <div className={styles["guide"]}>
        <h1 className={styles["guide-heading"]}>Guide</h1>
        <CardTwo customClass={styles["container"]}>
          <h2
            className={
              styles["h2-heading"] + " " + styles["h2-heading-" + theme]
            }
          >
            Single Player
          </h2>
          <p className={styles["p1"]}>
            Challenge-based progression with rating system and mobile-friendly
            touch controls.
          </p>

          <h3 className={styles["h3-heading"]}>In-Game Controls</h3>
          <ul>
            <li>Uses Player-0 controls</li>
            <li>On-screen buttons (mobile only)</li>
            <li>Gamepad: Press Start button to pause the game</li>
          </ul>

          <h3 className={styles["h3-heading"]}>Rating</h3>
          <ul>
            <li>
              Each challenge is rated out of 100. It is rated based on how close
              you are to the target score
            </li>
            <li>
              Your overall rating is the average of all your best score of each
              challenge
            </li>
          </ul>

          <h2
            className={
              styles["h2-heading"] + " " + styles["h2-heading-" + theme]
            }
          >
            Split Screen
          </h2>
          <p className={styles["p1"]}>Local 2-player competitive mode.</p>

          <h3 className={styles["h3-heading"]}>In-Game Controls</h3>
          <ul>
            <li>Player-0 and Player-1 controls both active</li>
            <li>Shared keyboard with different key sets for each player</li>
            <li>Up to 2 gamepads simultaneously</li>
            <li>Gamepad: Press Start button to pause the game</li>
          </ul>

          <h2
            className={
              styles["h2-heading"] + " " + styles["h2-heading-" + theme]
            }
          >
            Online Multiplayer
          </h2>
          <p className={styles["p1"]}>Up to 4 players in private rooms.</p>

          <h3 className={styles["h3-heading"]}>Game Modes</h3>
          <ul>
            <li>
              <b>Survival:</b> Speed increases gradually, last survivor wins
            </li>
            <li>
              <b>Speed Battle:</b> Speed increases gradually, highest scorer wins
            </li>
          </ul>

          <h3 className={styles["h3-heading"]}>In-Game Controls</h3>
          <ul>
            <li>Uses Player-0 controls</li>
            <li>Gamepad: Press Start button to exit the game to lobby</li>
          </ul>

          <h2
            className={
              styles["h2-heading"] + " " + styles["h2-heading-" + theme]
            }
          >
            General
          </h2>

          <h3 className={styles["h3-heading"]}>Scoring (All Modes)</h3>
          <ul>
            <li>
              <b>1x Clear:</b> 100 pts
            </li>
            <li>
              <b>2x Clear:</b> 400 pts
            </li>
            <li>
              <b>3x Clear:</b> 900 pts
            </li>
            <li>
              <b>4x Clear:</b> 1,600 pts
            </li>
          </ul>

          <h3 className={styles["h3-heading"]}>Navigation Controls</h3>
          <ul>
            <li>
              <b>Keyboard:</b> Arrow Up/Down to navigate, Enter to select,
              Escape to go back
            </li>
            <li>
              <b>Gamepad:</b> D-pad Up/Down to navigate, X to select, B to go
              back. Press X on input fields to activate virtual keyboard
            </li>
            <li>
              <b>Mouse:</b> Hover to focus, click to select, use go-back button
              to return
            </li>
          </ul>

          <h3 className={styles["h3-heading"]}>Control Customization</h3>
          <ul>
            <li>Customize all controls in Controls section</li>
          </ul>

          <h3 className={styles["h3-heading"]}>Game Data</h3>
          <ul>
            <li>
              <b>Current Status:</b> Challenge progress and control settings are
              saved locally on your device
            </li>
            <li>
              <b>Coming Soon:</b> Cloud sync mapped to your Google account - access
              your progress, challenges, and custom controls on ANY device, ANYWHERE
            </li>
            <li>
              <b>Store Purchases:</b> All store purchases are already mapped to your
              Google account and accessible across all your devices
            </li>
          </ul>

          <h2
            className={
              styles["h2-heading"] + " " + styles["h2-heading-" + theme]
            }
          >
            Purchasing Store Items
          </h2>
          <ul>
            <li>No account creation or registration required on Gumroad</li>
            <li>
              Use the email associated with your Google sign-in on Gumroad, then
              login here using the same email to check the status
            </li>
            <li>
              Complete payment, you will receive an instant email confirmation
              from Gumroad
            </li>
            <li>Theme unlocks automatically after payment verification</li>
            <li>
              It can take a few minutes to reflect in your account - take a
              chill pill, play a game and come back
            </li>
          </ul>

          <h2
            className={
              styles["h2-heading"] + " " + styles["h2-heading-" + theme]
            }
          >
            Contact Me
          </h2>
          <ul>
            <li>
              <a
                href="https://twitter.com/runtimeerror55"
                target="_blank"
                rel="noopener noreferrer"
                className={styles["contact-link"]}
              >
                Twitter
              </a>
            </li>
            <li>
              runtimeerror55@gmail.com
            </li>
          </ul>
        </CardTwo>
      </div>
    </CardOne>
  );
};
